/**
 * EXAM CREATION — one job per exam, one row per request.
 *
 * The one job type in this system whose input is a teacher's intent rather than a file: nothing has been
 * uploaded, so there is no page to split, no region to OCR and no structure to extract. What it produces
 * is the same shape everything downstream already consumes — `exam.tasks`, a blank exam, page images —
 * only declared by the document instead of inferred from a scan (see exam-creation/compile-exam.ts).
 *
 * A JOB RATHER THAN A TOOL CALL, for the ordinary reasons: it runs for minutes (figures dominate), it
 * costs real money per attempt, and it must survive a restart. As a row it has a lease, a bounded number
 * of attempts, and a lock that keeps it away from anything else rewriting the task tree.
 *
 * THE AGENT IS IMPORTED LAZILY, and that is not an optimisation. `db/index.ts` imports the job registry for
 * the `defineJob` side effect, and `client/startup.ts` imports `db/index.ts` — so every job definition is
 * evaluated in the BROWSER too. This one hangs off `exam-creation-agent.ts`, which reaches `compile-exam.ts`,
 * which calls `promisify(execFile)` at module scope: bundled for the browser that is a call to a shimmed-away
 * `undefined`, and it takes down the whole client at startup rather than just this feature. A dynamic import
 * inside the handler keeps the node-only half out of the client graph; the request type comes across as
 * `import type`, which is erased.
 *
 * IT WRITES NO `onStepStart`/`onStepComplete` — that machinery is for the UPLOAD pipeline's setup steps
 * (see step-completion.ts), and creation is not one of them: `taskExtraction` in particular means "we read
 * the tasks off the pages", which is exactly what did not happen here. What it DOES write, directly, is the
 * `task` step's own progress record — COMPLETED, on success — plus an auto-confirmed
 * `structureConfirmationState` (see `openStructureGate` in confirm-structure.ts): the tree this job just
 * wrote IS the confirmed structure, authored by the teacher in the exam-creator canvas rather than read off
 * a scan, and skipping that closes three gates that would otherwise wait on it forever — `msExtraction`,
 * `gsExtraction` and `answerExtraction` all wait rather than drop on an unconfirmed structure, and the
 * two generation steps' own preconditions never close without `task` at COMPLETED. The structure AGENT
 * itself still never runs for a generated exam — see `structureJob.ready`'s own `generatedExam` drop.
 *
 * IT DOES WRITE ITS OWN PROGRESS, separately: `examCreationState`, a plain `ConfirmationProgress` on
 * `exam.examState` (STARTED at the top of the handler, DONE on success, FAILED — and rethrown — on error;
 * see `setExamCreationProgressByExam` in confirmation-progress.ts). PENDING until then, which is also what
 * gates the exam-creator step in the sidebar (`showExamCreatorStep` in chat.tsx): an uploaded exam never
 * touches this field, so the step never appears for it.
 *
 * IT ALSO ANNOUNCES ITSELF IN THE CHAT, the same `pushWorkflowTask` mechanism `grading` uses for the same
 * reason (no `onStepComplete` of its own to hang an announcement off — see step-completion.ts and
 * grading.step.ts): a workflow pending task on success (naming any dropped materials or compile warnings,
 * worded differently for a fresh creation vs. a targeted `refinementInstruction` patch) or on failure — but
 * only once every attempt is spent, not on a retryable failure the next attempt might still recover from.
 * See exam-creation-finished.task.ts.
 */

import { logger } from '@adornis/base/logging.js';
import { ConfirmationProgressStatus, setExamCreationProgressByExam } from '../../../confirmation-progress.js';
import { seedProgressRecord, setStatusByExam, StepState } from '../../../extraction-progress.js';
import type { ExamCreationRequest } from '../../exam-creation/exam-creation-agent.js';
import { openStructureGate } from '../../confirm-structure.js';
import * as conditions from '../job-conditions.js';
import { taskTreeLockKey } from '../job.js';
import { defineJob } from '../registry.js';

export interface ExamCreationPayload extends ExamCreationRequest {
  /**
   * Create over an exam that already has tasks. Off by default: the tree is what master solutions,
   * grading schemes and student answers all hang off, so replacing it is a decision the teacher makes
   * explicitly, not a side effect of asking twice.
   */
  replaceExisting?: boolean;
  /**
   * PATCH an exam that already has tasks, instead of creating or replacing one — "add a task about X",
   * "make task 3 worth more", in the teacher's own words. Runs the Delta Change Planner turn first (see
   * `requestChangePlan`/`loadRefinementContext` in exam-creation-agent.ts) against the exam's own current
   * source and plan, so unrelated tasks and material are left untouched rather than regenerated from
   * scratch. Mutually exclusive with `replaceExisting` — a request to start over wants a fresh plan, not a
   * patch onto the old one; `replaceExisting` wins if both are set.
   */
  refinementInstruction?: string;
  [key: string]: unknown;
}

export const examCreationJob = defineJob<ExamCreationPayload>({
  type: 'examCreation',

  /**
   * One row from the caller's own payload — the request IS the unit. Declared anyway, because a type
   * without `batch` is one `dispatchJobs` refuses.
   *
   * `joinTwin` because a teacher who asks twice must be told about the run already going, not about
   * nothing: the second ask resolves to the live row. No `waitMs` — a creation takes minutes and the chat
   * turn must not hold for it.
   */
  batch: {
    units: (_examId, scope) => [scope as ExamCreationPayload],
    joinTwin: true,
  },

  /** It rewrites `exam.tasks` wholesale, so it excludes every other producer of the tree. */
  lockKey: examId => taskTreeLockKey(examId),

  dedupeKey: examId => `examCreation:${examId}`,

  /**
   * Figures dominate: up to three generation-plus-review rounds each, in parallel, then the source and up
   * to three repair rounds. Fifteen minutes is generous for that and still far below the point where a
   * crashed row would sit unreclaimable for longer than the work would have taken.
   */
  maxRuntimeMs: 15 * 60_000,

  /**
   * TWO, where most types get three. An attempt is a full plan plus every figure regenerated from
   * scratch, so a third attempt buys little against a systematic failure (a bad request, a model
   * refusing) and costs the most of any retry in the system.
   */
  maxAttempts: 2,

  ready: (exam, job) => {
    // Submissions are the hard stop, and it is not about clobbering a draft: student answers bind to
    // tasks by taskNumber, so rewriting the tree under existing submissions silently detaches them from
    // the answers already extracted. A teacher who wants a different exam wants a different exam.
    //
    // Asked through `conditions.hasSubmissions`, which reads the upload progress rather than
    // `exam.submissions` — the latter is a DataLoadedField resolved from another collection, so it is
    // absent from the document a condition receives and a length check on it would never fire.
    if (conditions.hasSubmissions(exam)) {
      return { verdict: 'drop', because: 'examCreation:submissions-exist' };
    }

    // Same reasoning extended to solution content instead of student answers: once a master solution or
    // grading scheme has been uploaded or generated, the tree is what THAT hangs off too, and rewriting it
    // — a fresh run or a refinement patch alike — would silently drop it. `create-exam.tool.ts` asks the
    // same question again before ever dispatching this job (so the teacher hears a refusal instead of a
    // silently dropped row), and `createExamAvailability` asks it a third time so the tool is not even
    // offered — see `isExamCreatorLocked`, the one definition all three share.
    if (conditions.examCreatorLocked(exam)) {
      return { verdict: 'drop', because: 'examCreation:locked' };
    }

    if (conditions.hasTasks(exam) && job.payload.replaceExisting !== true && !job.payload.refinementInstruction) {
      return { verdict: 'drop', because: 'examCreation:already-has-tasks' };
    }

    return 'run';
  },

  handler: async (examId, payload, context) => {
    // FIRST, before anything else can throw — this is what flips the exam-creator step from hidden to
    // "loading" in the sidebar, so the teacher sees it the moment the row actually starts running.
    //
    // `total`/`finished` RESET TO 0 HERE TOO, not left to `setExamCreationTotalByExam` alone — a
    // refinement run (`anpassungsanweisung`) starts on an exam whose PREVIOUS run left them at, say, 5/5
    // (100%). The real materials count isn't known until planning finishes a little later (see PHASE 2 in
    // exam-creation-agent.ts's `runExamCreation`), so without this the ring would flash full during that
    // gap instead of reading empty like a fresh run.
    await setExamCreationProgressByExam(examId, {
      status: ConfirmationProgressStatus.STARTED,
      total: 0,
      finished: 0,
    });

    // Server-only from here: see THE AGENT IS IMPORTED LAZILY at the top of this file.
    const { loadRefinementContext, runExamCreation } = await import('../../exam-creation/exam-creation-agent.js');

    let outcome: Awaited<ReturnType<typeof runExamCreation>>;
    try {
      // A patch onto the existing exam, not a fresh one — `replaceExisting` overrides it: asking to start
      // over should get a new plan, not a delta against the one being discarded.
      const refinement =
        payload.refinementInstruction && payload.replaceExisting !== true
          ? await loadRefinementContext(examId, payload.refinementInstruction)
          : null;

      outcome = await runExamCreation(examId, payload, {
        signal: context.signal,
        refinement: refinement ?? undefined,
      });
    } catch (err) {
      // Same shape as `markSplitAgentFailed` in split-file-agent.ts: swallow a failure to write the
      // failure, log it, and rethrow the real error so the row still fails and the runner still retries it.
      await setExamCreationProgressByExam(examId, {
        status: ConfirmationProgressStatus.FAILED,
        message: 'examCreationFailed',
      }).catch(progressErr => {
        logger.error({ err: progressErr, examId }, 'Failed to set examCreationState to FAILED');
      });

      // ONLY ON THE LAST ATTEMPT. `attempts` already counts this run (incremented at claim time — the
      // same convention runner.ts itself reads it by, see its own `job.attempts >= job.maxAttempts`
      // check), so a retryable failure stays silent and the teacher only hears about it if the retry
      // ALSO fails — announcing on every attempt would tell them it failed and then, a minute later,
      // that it actually worked.
      if (context.job.attempts >= context.job.maxAttempts) {
        try {
          const { pushWorkflowTask } = await import('../../pending-tasks/workflow-agent.js');
          const { createExamCreationFinishedPendingTask } = await import(
            '../../pending-tasks/exam-creation-finished.task.js'
          );
          await pushWorkflowTask(
            examId,
            context.exam.conversationId,
            createExamCreationFinishedPendingTask({ outcomeKind: 'failed' }),
          );
        } catch (announceErr) {
          logger.error({ err: announceErr, examId }, 'Failed to announce exam-creation failure');
        }
      }

      throw err;
    }

    await setExamCreationProgressByExam(examId, { status: ConfirmationProgressStatus.DONE });

    // The tree this job just wrote IS the confirmed structure — authored by the teacher in the
    // exam-creator canvas, not read off a scan. `seedProgressRecord` first: a `@Field` missing from
    // `initializeExamState` lands on the document as `null`, and `$set` on a field of a null element
    // fails in Mongo, so the very first creation on a fresh exam needs the record seeded before it can
    // be marked COMPLETED. Runs on the refinement path too, and both writes are idempotent.
    await seedProgressRecord(examId, 'task');
    await setStatusByExam(examId, 'task', StepState.COMPLETED, 'progress.taskStructureCreated');
    await openStructureGate(examId);

    logger.info(
      {
        examId,
        tasks: outcome.taskCount,
        pages: outcome.pageCount,
        repairRounds: outcome.repairRounds,
        rejectedMaterials: outcome.rejectedMaterials,
      },
      'Exam created',
    );

    // Reopens the exam-creator canvas once the exam is actually ready — the tool call that dispatched this
    // job already opened it (see create-exam.tool.ts's own `canvasToOpen`), but that only reaches whoever is
    // looking right now: a teacher who closed the canvas, switched exams, or asked from a fresh tab in the
    // several minutes this job runs would otherwise never see the finished exam without asking again. Same
    // `pendingCanvasOpen` mechanism as `reopenStructureCanvas` in confirm-structure.ts — a direct write, not
    // a tool result, because nothing is listening for THIS handler's return value on the client.
    try {
      const { getCollection } = await import('@adornis/baseql/server/collections.js');
      const { Conversation } = await import('../../../conversation.js');
      const conversationCollection = await getCollection(Conversation);
      await conversationCollection.updateOne(
        { _id: context.exam.conversationId },
        { $set: { pendingCanvasOpen: 'examcreator' } },
      );
    } catch (err) {
      logger.warn({ err, examId }, 'Failed to reopen exam creator canvas after exam creation');
    }

    // A figure that could not be produced changed a task; the teacher should hear about that.
    const warnings = outcome.problems.filter(problem => problem.severity === 'warning').map(problem => problem.message);

    // ANNOUNCES INTO THE CHAT, same `pushWorkflowTask` mechanism `announceRegradeFinished` uses for
    // `grading` — the other job type with no `onStepComplete` of its own. A refinement (targeted patch
    // via `refinementInstruction`, not a full replace) reads as "die Anpassung", not "die Klausur wurde
    // erstellt" — same distinction the handler already draws above for which context to load.
    try {
      const { pushWorkflowTask } = await import('../../pending-tasks/workflow-agent.js');
      const { createExamCreationFinishedPendingTask } = await import(
        '../../pending-tasks/exam-creation-finished.task.js'
      );
      await pushWorkflowTask(
        examId,
        context.exam.conversationId,
        createExamCreationFinishedPendingTask({
          outcomeKind: payload.refinementInstruction && payload.replaceExisting !== true ? 'refined' : 'created',
          taskCount: outcome.taskCount,
          pageCount: outcome.pageCount,
          rejectedMaterials: outcome.rejectedMaterials,
          warnings,
        }),
      );
    } catch (err) {
      logger.warn({ err, examId }, 'Failed to announce exam creation finished');
    }

    return {
      note: `created ${outcome.taskCount} tasks over ${outcome.pageCount} page(s)`,
      // Small and local, for whatever reports on the run: what this row produced, not what was asked.
      result: {
        taskCount: outcome.taskCount,
        pageCount: outcome.pageCount,
        repairRounds: outcome.repairRounds,
        rejectedMaterials: outcome.rejectedMaterials,
        warnings,
      },
    };
  },
});
